// frontend/src/components/HeroSection.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './HeroSection.css';
import amazonLogo from '../assets/images/amazon_logo.svg';
import ebayLogo from '../assets/images/ebay_logo.svg';

// Define your hero slides with a single image URL
const HERO_SLIDES = [
    {
        id: 'slide1',
        imageUrl: 'https://res.cloudinary.com/dit7yymcl/image/upload/v1772108202/finl_qiwbah.webp',
        headline: 'Discover Premium Spice Boxes',
        description: 'Perfect gifts for every culinary enthusiast.',
        buttonText: 'The Chai Lovers',
        buttonUrl: '/products/The%20Chai%20Lovers',
        objectPosition: 'center'
    },
    {
        id: 'slide2',
        imageUrl: 'https://res.cloudinary.com/dit7yymcl/image/upload/v1772118095/ChatGPT_Image_Feb_26_2026_08_29_53_PM_eluyqt.webp',
        headline: 'Our Pure Organic Collection',
        description: 'Sustainably sourced, naturally delicious.',
        buttonText: 'View Gift Box',
        buttonUrl: '/product/699349793822ad1f87daa7f8',
        objectPosition: 'center'
    }
];

const AUTO_SLIDE_INTERVAL = 7500; // Slide changes every 6 seconds
const MIN_SWIPE_DISTANCE = 50; // Minimum distance in pixels for a valid swipe

const HeroSection = () => {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const navigate = useNavigate();
    const autoPlayIntervalRef = useRef(null); 
    const isSwiping = useRef(false); // NEW: Track if the user is swiping vs tapping

    // State for swipe functionality
    const [touchStartX, setTouchStartX] = useState(0);
    const [touchEndX, setTouchEndX] = useState(0);

    // Function to advance to the next slide
    const nextSlide = useCallback(() => {
        setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % HERO_SLIDES.length);
    }, []);

    // Function to go to the previous slide
    const prevSlide = useCallback(() => {
        setCurrentSlideIndex((prevIndex) =>
            (prevIndex - 1 + HERO_SLIDES.length) % HERO_SLIDES.length
        );
    }, []);

    // Function to go to a specific slide by index
    const goToSlide = useCallback((index) => {
        setCurrentSlideIndex(index);
        resetAutoPlayInterval(); 
    }, [nextSlide]);

    // Function to reset the auto-play interval
    const resetAutoPlayInterval = useCallback(() => {
        if (autoPlayIntervalRef.current) {
            clearInterval(autoPlayIntervalRef.current);
        }
        if (HERO_SLIDES.length > 1) {
            autoPlayIntervalRef.current = setInterval(nextSlide, AUTO_SLIDE_INTERVAL);
        }
    }, [nextSlide]);

    // Automatic slide transition and interval reset logic
    useEffect(() => {
        resetAutoPlayInterval(); 
        return () => {
            if (autoPlayIntervalRef.current) {
                clearInterval(autoPlayIntervalRef.current);
            }
        };
    }, [currentSlideIndex, resetAutoPlayInterval]); 

    // Handle button click dynamically based on current slide's buttonUrl (For Desktop)
    const handleButtonClick = useCallback((e) => {
        e.stopPropagation(); 
        
        const currentSlide = HERO_SLIDES[currentSlideIndex];
        if (currentSlide && currentSlide.buttonUrl) {
            navigate(currentSlide.buttonUrl);
        } else {
            navigate('/products');
        }
    }, [currentSlideIndex, navigate]);

    // --- NEW: Handle entire section click (For Mobile) ---
    const handleSectionClick = useCallback((e) => {
        // Ignore if the desktop button was clicked
        if (e.target.closest('.hero-button')) return;

        // If the user was just swiping left/right, don't trigger the click!
        if (isSwiping.current) {
            isSwiping.current = false; // Reset the flag
            return;
        }

        // Only trigger the full-image click on mobile screens
        if (window.innerWidth <= 768) {
            const currentSlide = HERO_SLIDES[currentSlideIndex];
            navigate(currentSlide.buttonUrl || '/products');
        }
    }, [currentSlideIndex, navigate]);

    // Touch event handlers for swipe
    const handleTouchStart = useCallback((e) => {
        setTouchStartX(e.targetTouches[0].clientX);
        setTouchEndX(e.targetTouches[0].clientX); // Initialize endX to startX to properly register clean taps
        isSwiping.current = false; // Reset swiping state on new touch
    }, []);

    const handleTouchMove = useCallback((e) => {
        setTouchEndX(e.targetTouches[0].clientX);
    }, []);

    const handleTouchEnd = useCallback((e) => {
        if (e.target.closest('.hero-button')) {
            return; 
        }

        const diffX = touchStartX - touchEndX; 

        if (Math.abs(diffX) > MIN_SWIPE_DISTANCE) {
            isSwiping.current = true; // Mark as swiped so the click handler ignores it
            e.preventDefault(); 
            if (diffX > 0) { 
                nextSlide();
            } else { 
                prevSlide();
            }
            resetAutoPlayInterval(); 
        }
        
        setTouchStartX(0);
        setTouchEndX(0);
    }, [touchStartX, touchEndX, nextSlide, prevSlide, resetAutoPlayInterval]);

    const currentSlideContent = HERO_SLIDES[currentSlideIndex];

    if (!currentSlideContent) {
        return <section className="hero-section fallback-content">Loading hero content...</section>;
    }

    return (
        <section
            className="hero-section"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={handleSectionClick} // <--- NEW: Added onClick handler to the whole section
        >
            <div className="hero-image-container">
                {HERO_SLIDES.map((slide, index) => (
                    <div
                        key={slide.id} 
                        className={`hero-image-wrapper ${index === currentSlideIndex ? 'active' : ''}`}
                    >
                        <img
                            src={slide.imageUrl}
                            alt={slide.headline}
                            className="hero-background-image"
                            style={{ objectPosition: slide.objectPosition || 'center' }}
                            onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/1920x1080/E0E0E0/ADADAD?text=Image+Unavailable'; }}
                        />
                    </div>
                ))}
            </div>

            <div key={currentSlideContent.id + '-content'} className="hero-content">
                <h1 className="hero-headline">{currentSlideContent.headline}</h1>
                {currentSlideContent.description && (
                    <p className="hero-description">{currentSlideContent.description}</p>
                )}
                {/* Desktop Button - Hidden on mobile via CSS */}
                <button className="hero-button" onClick={handleButtonClick}>
                    {currentSlideContent.buttonText}
                </button>
            </div>

            {HERO_SLIDES.length > 1 && (
                <div className="slider-dots">
                    {HERO_SLIDES.map((_, index) => (
                        <span
                            key={index}
                            className={`dot ${index === currentSlideIndex ? 'active' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation(); // Stop dots from triggering full-section click
                                goToSlide(index);
                            }}
                            aria-label={`Go to slide ${index + 1}`}
                        ></span>
                    ))}
                </div>
            )}

            <div className="store-availability-message">
                <span>Buy on</span>
                <img src={amazonLogo} alt="Amazon" className="store-logo-small" />
                <span>&amp;</span>
                <img src={ebayLogo} alt="eBay" className="store-logo-small" />
            </div>
        </section>
    );
};

export default HeroSection;